/**
 * A watcher source's push endpoint (sources.md "watcher"): on Pro the server issues a push URL
 * (`POST /api/push-tokens` → `{ url, hint }`, shown once) that any tool can POST items to; the mobile
 * apps' feeder is not in the web build. "Send a test item" pushes a sample through
 * `engine.sources.push`, so the whole ingest path runs and a message appears.
 */

import { useState } from 'react';
import type { PushTokenCreateResponse, Source } from '@digester/core';
import { reportError } from '../../app/errors.js';
import { useServices } from '../../app/services.js';
import { showToast } from '../../app/Toast.js';
import { useEngine } from '../../engine-client/EngineProvider.js';
import { Icon } from '../../icons/Icon.js';
import { Section } from '../../ui/form.js';
import { describeSourceError } from './errors.js';

export function WatcherPanel({ source }: { source: Source }) {
  const engine = useEngine();
  const { api } = useServices();
  const [endpoint, setEndpoint] = useState<PushTokenCreateResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const createUrl = async (): Promise<void> => {
    setBusy(true);
    setError(null);
    try {
      // The server issues a push URL only for a source its copy already has: sync first.
      await engine.sync.now().catch(() => undefined);
      setEndpoint(await api.call<PushTokenCreateResponse>('pushTokenCreate', { body: { sourceId: source.id } }));
    } catch (e) {
      const code = (e as { code?: string }).code;
      if (code === 'plan_limit' || code === 'not_entitled') reportError(e);
      else setError(code === 'unauthenticated' ? 'Sign in with a Pro plan to receive pushes on the server.' : describeSourceError(e));
    } finally {
      setBusy(false);
    }
  };

  const sendTest = async (): Promise<void> => {
    const now = Date.now();
    try {
      const report = await engine.sources.push(source.id, [
        {
          externalId: `watcher-test-${now}`,
          title: `Test item for ${source.name}`,
          text: 'This message was pushed by “Send a test item”. Watchers receive items like this from a feeder or a webhook.',
          publishedAt: now as never,
        },
      ]);
      showToast(report.inserted > 0 ? 'Test item delivered to Inbox' : report.overLimit > 0 ? 'Not delivered: the daily message limit is reached' : 'Test item already delivered');
    } catch (e) {
      reportError(e);
    }
  };

  const copy = async (text: string): Promise<void> => {
    try {
      await navigator.clipboard.writeText(text);
      showToast('Copied');
    } catch {
      showToast('Select the text and copy it');
    }
  };

  const curl = endpoint ? `curl -X POST '${endpoint.url}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"items":[{"externalId":"post-1","title":"Hello","text":"First pushed item"}]}'` : '';

  return (
    <Section title="Push" help="A watcher is fed by push only.">
      <div className="read-only-row">
        <span>Feeder app</span>
        <strong>Available in the mobile apps</strong>
      </div>
      <div className="watcher-endpoint">
        {endpoint ? (
          <>
            <label className="field">
              <span>Push URL · shown once</span>
              <input type="text" readOnly value={endpoint.url} data-push-url onFocus={(e) => e.target.select()} />
            </label>
            <div className="watcher-buttons">
              <button type="button" className="secondary-button" onClick={() => void copy(endpoint.url)}>
                <Icon name="copy" /> Copy URL
              </button>
              <button type="button" className="secondary-button" onClick={() => void copy(curl)}>
                <Icon name="terminal" /> Copy curl example
              </button>
            </div>
            <pre className="watcher-curl">{curl}</pre>
            <p className="field-help">Keep it secret: anyone with this URL can add messages. Create a new one to revoke it (ends with …{endpoint.hint}).</p>
          </>
        ) : (
          <>
            <button type="button" className="secondary-button" disabled={busy} onClick={() => void createUrl()}>
              <Icon name="link" /> Create push URL
            </button>
            <p className="field-help">Pro: the server receives pushed items and your devices get them by sync. The URL works once this source has synced.</p>
          </>
        )}
        {error ? (
          <div className="test-error" role="alert">
            <Icon name="circle-alert" />
            <span>{error}</span>
          </div>
        ) : null}
      </div>
      <button type="button" className="secondary-button" data-action="send-test-item" onClick={() => void sendTest()}>
        <Icon name="send" /> Send a test item
      </button>
    </Section>
  );
}
